import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, OnInit, inject, input } from '@angular/core';
import { FormlyConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, isObservable, of } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';

import { FormsService } from 'ish-shared/forms/utils/forms.service';

/**
 * Component that reads a fields validity status and displays active error messages.
 */
@Component({
  selector: 'ish-validation-message',
  template:
    '<small class="mt-1" id="{{ field().id }}-validation-error">{{ errorMessage$ | async | translate }} </small>',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [AsyncPipe, TranslateModule],
})
export class ValidationMessageComponent implements OnInit, OnChanges, OnDestroy {
  private config = inject(FormlyConfig);

  readonly field = input.required<FormlyFieldConfig>();
  readonly id = input<string>(undefined);
  errorMessage$: Observable<string>;

  ngOnInit(): void {
    this.field().props = {
      ...this.field().props,
      ariaDescribedByIds: FormsService.addAriaDescribedById(
        this.field().props?.ariaDescribedByIds,
        `${this.field().id}-validation-error`
      ),
    };
  }

  ngOnDestroy(): void {
    this.field().props.ariaDescribedByIds = FormsService.removeAriaDescribedById(
      this.field().props.ariaDescribedByIds,
      `${this.field().id}-validation-error`
    );
  }

  ngOnChanges() {
    this.errorMessage$ = this.field().formControl.statusChanges.pipe(
      startWith(false),
      switchMap(() => (isObservable(this.errorMessage) ? this.errorMessage : of(this.errorMessage)))
    );
  }

  get errorMessage(): string | Observable<string> {
    const fieldForm = this.field().formControl;
    for (const error in fieldForm.errors) {
      if (fieldForm.errors.hasOwnProperty(error)) {
        // eslint-disable-next-line unicorn/no-null
        if (fieldForm.errors[error] !== null && typeof fieldForm.errors[error] === 'object') {
          if (fieldForm.errors[error].errorPath) {
            return;
          }

          if (fieldForm.errors[error].message) {
            return fieldForm.errors[error].message;
          }
        }

        return this.determineErrorMessage(error);
      }
    }
  }

  private determineErrorMessage(error: string): string | Observable<string> {
    let message = this.config.getValidatorMessage(error);

    const field = this.field();
    if (field.validation?.messages && field.validation.messages[error]) {
      message = field.validation.messages[error];
    }

    if (field.validators?.[error]?.message) {
      message = field.validators[error].message;
    }

    if (field.asyncValidators?.[error]?.message) {
      message = field.asyncValidators[error].message;
    }

    if (typeof message === 'function') {
      return message(field.formControl.errors[error], field);
    }

    return message;
  }
}
